import { API_BASE_URL } from '../config/api'
import { tokenService } from '../services/tokenService'

export interface ApiError {
  detail?: string
  error?: string
  code?: string
  message?: string
}

/**
 * Intercept API responses and handle 2FA requirements
 * This utility can be used to wrap fetch calls and automatically handle 2FA_REQUIRED errors
 */
export async function interceptApiResponse<T>(
  response: Response,
  retryRequest?: () => Promise<Response>
): Promise<T> {
  if (!response.ok) {
    try {
      const errorData: ApiError = await response.json().catch(() => ({}))
      
      // Check for 2FA requirements
      if (
        errorData.code === '2FA_REQUIRED' ||
        errorData.error === 'Two-factor authentication required' ||
        response.status === 403 && errorData.code === '2FA_REQUIRED'
      ) {
        // Return a special error that can be caught and handled by the caller
        throw new TwoFactorRequiredError(errorData.message || 'Two-factor authentication required')
      }
      
      // Check for 2FA setup requirement
      if (
        errorData.code === '2FA_SETUP_REQUIRED' ||
        errorData.error === 'Two-factor authentication setup required'
      ) {
        throw new TwoFactorSetupRequiredError(
          errorData.message || 'Two-factor authentication setup required'
        )
      }
      
      // For other errors, throw a standard error
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`)
    } catch (error) {
      // If it's already a 2FA error, re-throw it
      if (error instanceof TwoFactorRequiredError || error instanceof TwoFactorSetupRequiredError) {
        throw error
      }
      // Otherwise, throw a generic error
      throw error
    }
  }
  
  // Handle successful responses
  if (response.status === 204) {
    return undefined as unknown as T
  }
  
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return await response.json()
  }
  
  return await response.text() as unknown as T
}

/**
 * Make an authenticated API request with 2FA error handling
 */
export async function makeAuthenticatedRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  retryWith2FA?: (code: string) => Promise<Response>
): Promise<T> {
  try {
    const token = await tokenService.getToken()
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    })
    
    return await interceptApiResponse<T>(response, retryWith2FA ? () => {
      // This would be called if we need to retry after 2FA verification
      // For now, we'll throw the error and let the caller handle it
      throw new TwoFactorRequiredError('Two-factor authentication required')
    } : undefined)
  } catch (error) {
    if (error instanceof TwoFactorRequiredError || error instanceof TwoFactorSetupRequiredError) {
      throw error
    }
    throw error
  }
}

/**
 * Custom error class for 2FA requirements
 */
export class TwoFactorRequiredError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TwoFactorRequiredError'
  }
}

/**
 * Custom error class for 2FA setup requirements
 */
export class TwoFactorSetupRequiredError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TwoFactorSetupRequiredError'
  }
}






